import {
	BIENNIAL_LABELS,
	COFFEE_LABELS,
	PLAN_NUTRIENTS,
	RANGE_MODE_LABELS,
	REGION_LABELS,
	SEASON_LABELS,
} from '../constants/parameters.constants';
import type {
	CoffeeType,
	RangeCombination,
	RangeMode,
	RangeRow,
	RangesKind,
	ReferenceBiennial,
	ReferenceRegion,
	ReferenceSeason,
	StateReferenceDTO,
	StateReferenceFixedRange,
	StateReferenceRange,
} from '../types/parameters.types';
import { setAtPath } from './draftPath';

export interface RangeCombinationInput {
	coffeeType: CoffeeType;
	mode: RangeMode;
	region: ReferenceRegion;
	biennial: ReferenceBiennial;
	season: ReferenceSeason;
	rows: RangeRow[];
}

type RangeCombinationResult =
	| { ok: true; value: StateReferenceRange[] }
	| { ok: false; error: string };

export const listRangeCombinations = (
	profiles: StateReferenceRange[],
	key: 'criticalRanges' | 'drisParams'
): RangeCombination[] => {
	const combinations: RangeCombination[] = [];

	profiles.forEach((profile, i) => {
		const title = COFFEE_LABELS[profile.coffeeType] ?? profile.coffeeType;
		const base = { coffeeType: profile.coffeeType, title };

		if (profile.fixedRanges?.length) {
			combinations.push({
				...base,
				id: `${key}-${i}-general`,
				mode: 'general',
				context: RANGE_MODE_LABELS.general,
				ranges: profile.fixedRanges,
				path: [key, i, 'fixedRanges'],
			});
		}
		(profile.dependentRegions ?? []).forEach((item, j) => {
			combinations.push({
				...base,
				id: `${key}-${i}-region-${j}`,
				mode: 'region',
				context: `${REGION_LABELS[item.region] ?? item.region} · Bienalidade ${BIENNIAL_LABELS[item.biennial] ?? item.biennial}`,
				ranges: item.fixedRanges,
				path: [key, i, 'dependentRegions', j, 'fixedRanges'],
			});
		});
		(profile.dependentSeasons ?? []).forEach((item, j) => {
			combinations.push({
				...base,
				id: `${key}-${i}-season-${j}`,
				mode: 'season',
				context: SEASON_LABELS[item.season] ?? item.season,
				ranges: item.fixedRanges,
				path: [key, i, 'dependentSeasons', j, 'fixedRanges'],
			});
		});
	});

	return combinations;
};

export const blankRangeRows = (kind: RangesKind): RangeRow[] => {
	if (kind === 'foliar') {
		return PLAN_NUTRIENTS.map((code) => ({ code, values: [undefined, undefined, undefined, undefined] }));
	}
	return PLAN_NUTRIENTS.flatMap((numerator) =>
		PLAN_NUTRIENTS.filter((denominator) => denominator !== numerator).map((denominator) => ({
			code: `${numerator}_${denominator}`,
			values: [undefined, undefined],
		}))
	);
};

export const rowsFromRanges = (ranges: StateReferenceFixedRange[]): RangeRow[] =>
	ranges.map((item) => ({ code: item.code, values: [...item.range] }));

const rowsToRanges = (rows: RangeRow[]): StateReferenceFixedRange[] =>
	rows.map((row) => ({ code: row.code, range: row.values as number[] }));

export const updateRangeCombination = (
	reference: StateReferenceDTO,
	key: 'criticalRanges' | 'drisParams',
	combination: RangeCombination,
	rows: RangeRow[]
): StateReferenceRange[] => setAtPath(reference, combination.path, rowsToRanges(rows))[key];

export const addRangeCombination = (
	profiles: StateReferenceRange[],
	input: RangeCombinationInput
): RangeCombinationResult => {
	const fixedRanges = rowsToRanges(input.rows);

	const index = profiles.findIndex((profile) => profile.coffeeType === input.coffeeType);
	const profile: StateReferenceRange =
		index >= 0
			? { ...profiles[index] }
			: { coffeeType: input.coffeeType, fixedRanges: [], dependentRegions: [], dependentSeasons: [] };

	if (input.mode === 'general') {
		if (profile.fixedRanges?.length) return { ok: false, error: 'Já existe uma combinação geral para este café.' };
		profile.fixedRanges = fixedRanges;
	}
	if (input.mode === 'region') {
		const regions = profile.dependentRegions ?? [];
		if (regions.some((item) => item.region === input.region && item.biennial === input.biennial)) {
			return { ok: false, error: 'Esta combinação de café, região e bienalidade já existe.' };
		}
		profile.dependentRegions = [...regions, { region: input.region, biennial: input.biennial, fixedRanges }];
	}
	if (input.mode === 'season') {
		const seasons = profile.dependentSeasons ?? [];
		if (seasons.some((item) => item.season === input.season)) {
			return { ok: false, error: 'Esta combinação de café e estação já existe.' };
		}
		profile.dependentSeasons = [...seasons, { season: input.season, fixedRanges }];
	}

	return {
		ok: true,
		value: index >= 0 ? profiles.map((item, i) => (i === index ? profile : item)) : [...profiles, profile],
	};
};

export const removeRangeCombination = (
	profiles: StateReferenceRange[],
	combination: RangeCombination
): StateReferenceRange[] => {
	const profileIndex = combination.path[1] as number;
	const itemIndex = combination.path[3] as number | undefined;

	return profiles
		.map((profile, i) => {
			if (i !== profileIndex) return profile;
			if (combination.mode === 'general') return { ...profile, fixedRanges: [] };
			if (combination.mode === 'region') {
				return { ...profile, dependentRegions: profile.dependentRegions?.filter((_, j) => j !== itemIndex) };
			}
			return { ...profile, dependentSeasons: profile.dependentSeasons?.filter((_, j) => j !== itemIndex) };
		})
		.filter(
			(profile) =>
				profile.fixedRanges?.length || profile.dependentRegions?.length || profile.dependentSeasons?.length
		);
};
